import React from 'react';
import { style } from 'typestyle';

import { SaveAndRevertButtons } from './SaveAndRevertButtons';
import { SettingsCoordinatorContext } from './SettingsCoordinatorContexts';
import { useSettingsCoordinatorStateInternal } from './SettingsCoordinatorHook';
import { SectionContainerBaseProps, SettingsSectionList } from './SettingsSectionList';

export type SettingsCoordinatorProps<T> = T
  & Partial<Pick<SectionContainerBaseProps<any>, 'sections'>>
  & {
  /**
   * Handler for when all child forms have submitted successfully
   */
  readonly onSubmit?: () => Promise<unknown>;
  /**
   * Handler for when any form has failed to submit
   */
  readonly onSubmitFailure?: (error: any) => Promise<void>;
  /**
   * Handler for when reset to default has succeeded. Only used by server settings so far
   */
  readonly onResetToDefaultSuccess?: () => Promise<void>;
  /**
   * Handler for when reset to default has failed. Only used by server settings so far
   */
  readonly onResetToDefaultFailure?: () => Promise<void>;
  /**
   * Specify whether to show or hide the reset button at the bottom of the settings page
   */
  readonly showResetToDefaultButton?: boolean;
  /**
   * if true the sections will be allowed to grow to full width of the screen
   */
  readonly allowFullWidthSections?: boolean;
  /**
   * if true, form submit requests for each settings section will be sent sequentially instead of in parallel
   */
  readonly submitSequentially?: boolean;
  /**
   * if true, the form content flows into the outer page scroll instead of creating its own nested
   * `overflow: auto` scroll region. Use for pages whose content height is dynamic (e.g. the Bridge
   * Routing Filters grouped views, which grow tall when accordion groups expand) — otherwise the nested
   * scroll region overflows once content exceeds the viewport and renders a second scrollbar alongside
   * the page scrollbar (a jarring double scrollbar).
   */
  readonly allowContentOverflow?: boolean;
};

/**
 * A reusable settings coordinator component with save and revert buttons. It provides supports for descendant
 * components to register callbacks to update their dirty, valid, and submitting states, and to handle submit and
 * revert clicks from this component. Currently, Formik forms are directly supported. Use the
 * {@link useSettingsCoordinator} hook to wire a Formik form nested at any level within a SettingsCoordinator
 * @param props
 * @constructor
 */
export function SettingsCoordinator<Props>(props: React.PropsWithChildren<SettingsCoordinatorProps<Props>>): JSX.Element {
  const {dispatch, triggerResetDefault, ...saveRevertProps} = useSettingsCoordinatorStateInternal(
    props.onSubmit,
    props.onSubmitFailure,
    props.onResetToDefaultSuccess,
    props.onResetToDefaultFailure,
    props.submitSequentially
  );

  return <>
    <SaveAndRevertButtons {...saveRevertProps}/>
    <SettingsCoordinatorContext.Provider value={dispatch}>
      <div className={props.allowContentOverflow ? settingsFormNoScrollStyle : settingsFormStyle}>
        {props.children && <div className={props.allowFullWidthSections ? settingsSectionListFullWidthStyle : settingsSectionListStyle}>
          {props.children}
        </div>}
        {props.sections?.length && <SettingsSectionList
          sections={props.sections}
          settings={props}
          formStyle={settingsSectionListStyle}
        />}
        <SaveAndRevertButtons {...saveRevertProps} triggerResetDefault={props.showResetToDefaultButton ? triggerResetDefault : null}/>
      </div>
    </SettingsCoordinatorContext.Provider>
  </>;
}

export const settingsFormStyle = style({
  flex: 1,
  paddingTop: 30,
  overflow: 'auto',
});

// Same as settingsFormStyle but without the nested `overflow: auto` scroll region, so content flows into
// the outer page scroll. Used by pages with dynamically-growing content (see `allowContentOverflow`) to
// avoid a double scrollbar (nested scroll region inside the page scroll).
export const settingsFormNoScrollStyle = style({
  flex: 1,
  paddingTop: 30,
});

export const settingsSectionListStyle = style({
  maxWidth: 508, // should verify where these values came from
  width: '100%',
  marginLeft: 'min(85px, 5vw)',
});

export const settingsSectionListFullWidthStyle = style({
  marginLeft: 'min(85px, 5vw)',
  marginRight: 'min(85px, 5vw)',
  flexGrow: 1
});
